import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useLabels } from 'wsm-common-data';
import { mergeLogos } from '../utils/mergeLogos';
import { createBrandTile } from '../utils/brandTile';

/**
 * Custom hook to access group logos from Redux store.
 * Merges OEM logos (from brand-assets-service) with custom tiles
 * (from item-list-service) alphabetically by brand name.
 *
 * @param {boolean} showLabel - Whether to show the tile label
 * @returns {Array<Object>} Merged, sorted array of tile objects
 */
export const useGroupLogos = (showLabel = false) => {
	const labels = useLabels();
	const { logoPaths, franchiseList, customLogos } = useSelector((state) => ({
		logoPaths: state.groupLogos?.logoPaths || [],
		franchiseList: state.franchiseList?.franchiseList || [],
		customLogos: state.customLogos?.customLogos || []
	}));

	return useMemo(() => {
		// Build OEM tiles from franchiseList + logoPaths
		const oemTiles = franchiseList.map((franchiseName) => {
			const franchiseNameForLogoPath = franchiseName
				.toLowerCase()
				.replace(/\s+/g, '-');

			const matchingLogo = logoPaths.find((path) =>
				path.toLowerCase().includes(`/${franchiseNameForLogoPath}/`)
			);

			return createBrandTile(franchiseName, matchingLogo, showLabel);
		});

		// Resolve ux-label-field i18n keys to actual values for custom tiles
		const resolvedCustomLogos = customLogos.map((item) => ({
			...item,
			name: labels.get(item.name) || item.name
		}));

		return mergeLogos(oemTiles, resolvedCustomLogos, showLabel);
	}, [logoPaths, franchiseList, customLogos, showLabel, labels]);
};
